/*
  fs-auto-discount.js
  Keeps "auto" discount codes in step with what is in the cart.

  A product form adds the hidden line property `_auto_discount`, filled from
  the product metafield custom.auto_discount_code (see sections/fs-duo-offer
  and snippets/buy-buttons). Whenever the cart changes this script:
    - applies any such code that is not on the cart yet
    - removes codes it applied once the lines that carried them are gone
  Codes the customer typed in themselves are left alone.

  Uses the Ajax Cart API `discount` parameter on /cart/update.js and the
  theme's pub/sub (PUB_SUB_EVENTS.cartUpdate).
*/
(function () {
  'use strict';

  var PROPERTY = '_auto_discount';
  var STORAGE_KEY = 'fs-auto-discount:applied';
  var routes = window.routes || {};
  var cartJsonUrl = (routes.cart_url || '/cart') + '.js';
  var cartUpdateUrl = routes.cart_update_url || '/cart/update';
  var script = document.currentScript;
  var initialItemCount = script ? parseInt(script.dataset.cartItems, 10) : NaN;
  var running = false;
  var queued = false;

  function normalise(code) {
    return String(code || '').trim().toUpperCase();
  }

  function readApplied() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function writeApplied(codes) {
    try {
      if (codes.length) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(codes));
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* storage unavailable: codes are still applied, only clean-up is best effort */
    }
  }

  function unique(codes) {
    return codes.filter(function (code, index) {
      return code && codes.indexOf(code) === index;
    });
  }

  function wantedCodes(cart) {
    return unique(
      (cart.items || []).map(function (item) {
        return item.properties ? normalise(item.properties[PROPERTY]) : '';
      })
    );
  }

  function appliedCodes(cart) {
    return unique(
      (cart.discount_codes || []).map(function (discount) {
        return normalise(discount.code);
      })
    );
  }

  /* Swap the drawer's sections in place. Unlike CartDrawer.renderContents this does not open the drawer. */
  function refreshDrawer(drawer, sections, response) {
    if (!drawer || !sections || !response || !response.sections) return;
    sections.forEach(function (section) {
      var element = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      var html = response.sections[section.id];
      if (!element || !html) return;
      element.innerHTML = drawer.getSectionInnerHTML(html, section.selector);
    });
    var overlay = drawer.querySelector('#CartDrawer-Overlay');
    if (overlay && typeof drawer.close === 'function') {
      overlay.addEventListener('click', drawer.close.bind(drawer));
    }
  }

  function sync(cart) {
    var wanted = wantedCodes(cart);
    var applied = appliedCodes(cart);
    var ours = readApplied();
    var missing = wanted.filter(function (code) {
      return applied.indexOf(code) === -1;
    });
    var stale = ours.filter(function (code) {
      return wanted.indexOf(code) === -1 && applied.indexOf(code) !== -1;
    });

    if (!missing.length && !stale.length) {
      writeApplied(
        ours.filter(function (code) {
          return applied.indexOf(code) !== -1;
        })
      );
      return Promise.resolve();
    }

    var next = applied
      .filter(function (code) {
        return stale.indexOf(code) === -1;
      })
      .concat(missing);

    var drawer = document.querySelector('cart-drawer');
    var sections =
      drawer && typeof drawer.getSectionsToRender === 'function' && typeof drawer.getSectionInnerHTML === 'function'
        ? drawer.getSectionsToRender()
        : null;

    var body = { discount: next.join(',') };
    if (sections) {
      body.sections = sections
        .map(function (section) {
          return section.id;
        })
        .join(',');
      body.sections_url = window.location.pathname;
    }

    return fetch(cartUpdateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(body),
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (updated) {
        var nowApplied = appliedCodes(updated);
        writeApplied(
          unique(
            ours
              .filter(function (code) {
                return stale.indexOf(code) === -1;
              })
              .concat(
                missing.filter(function (code) {
                  return nowApplied.indexOf(code) !== -1;
                })
              )
          )
        );
        refreshDrawer(drawer, sections, updated);
      });
  }

  function run() {
    if (running) {
      queued = true;
      return;
    }
    running = true;
    fetch(cartJsonUrl, { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(sync)
      .catch(function (error) {
        console.error('fs-auto-discount:', error);
      })
      .then(function () {
        running = false;
        if (queued) {
          queued = false;
          run();
        }
      });
  }

  function init() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        run();
      });
    }
    /* Nothing to apply or clean up on an empty cart we never touched. */
    if (initialItemCount === 0 && readApplied().length === 0) return;
    run();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
